import { useAppDispatch } from '@/app/hooks';
import type { FileManagerItem } from '@/components/MantineFileManager/file.type';
import {
  useDeleteFileMutation,
  useDeleteFilesMutation,
} from '@/features/api/endpoints/fileEndpoints';
import { useDeleteFolderMutation } from '@/features/api/endpoints/folderEndpoints';
import { clearSelection } from '@/features/drive/driveSlice';
import { useIsAdmin } from '@/shared/auth/useDirectusUser';
import { useIsXs } from '@/shared/utils/mediaQuery-utils';
import { ActionIcon, Button, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';

type DeleteSelectedBtnProps = {
  selectedItems: FileManagerItem[];
};

const DeleteSelectedBtn = ({ selectedItems }: DeleteSelectedBtnProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslate();
  const isXs = useIsXs();
  const isAdmin = useIsAdmin();

  const [deleteFolder] = useDeleteFolderMutation();
  const [deleteFile] = useDeleteFileMutation();
  const [deleteFiles] = useDeleteFilesMutation();

  if (!isAdmin) {
    return null;
  }

  const handleDeleteSelected = () => {
    const selectedFiles = selectedItems.filter(item => item.kind === 'file').map(item => item.id);
    const selectedFolders = selectedItems
      .filter(item => item.kind === 'folder')
      .map(item => item.id);

    if (!selectedFiles.length && !selectedFolders.length) {
      return;
    }

    modals.openConfirmModal({
      title: t('drive.deleteSelected.title'),
      fullScreen: isXs,
      children: <Text size="sm">{t('drive.deleteSelected.message')}</Text>,
      labels: {
        cancel: t('common.cancel'),
        confirm: t('common.delete'),
      },
      confirmProps: { color: 'red' },
      centered: true,
      onConfirm: async () => {
        try {
          if (selectedFiles.length) {
            if (selectedFiles.length === 1) {
              await deleteFile(selectedFiles[0]).unwrap();
            } else {
              await deleteFiles(selectedFiles).unwrap();
            }
          }

          for (const folderId of selectedFolders) {
            await deleteFolder(folderId).unwrap();
          }

          dispatch(clearSelection());
          notifications.show({
            color: 'green',
            message: t('drive.deleteSelected.success'),
          });
        } catch {
          notifications.show({
            color: 'red',
            message: t('drive.deleteSelected.error'),
          });
        }
      },
    });
  };

  return (
    <>
      <Tooltip label={t('drive.deleteSelected.title')} withArrow>
        <ActionIcon
          onClick={handleDeleteSelected}
          aria-label={t('drive.deleteSelected.title')}
          hiddenFrom="sm"
          variant="light"
        >
          <IconTrash size={18} />
        </ActionIcon>
      </Tooltip>
      <Button
        leftSection={<IconTrash size={18} />}
        variant="light"
        color="red"
        disabled={!selectedItems.length}
        onClick={handleDeleteSelected}
        visibleFrom="sm"
      >
        {t('drive.deleteSelected.title')}
      </Button>
    </>
  );
};

export default DeleteSelectedBtn;
